import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const tabsListVariants = cva("", {
  variants: {
    variant: {
      default:
        "inline-flex h-auto w-full flex-wrap items-center justify-center gap-2 bg-background p-2 text-muted-foreground",
      // The overlay's campaign rail: a scrolling column of chips docked
      // beside the game, or a row in the portrait top bar.
      rail: "flex min-h-0 min-w-0 flex-1 items-center gap-2 data-[orientation=horizontal]:h-full data-[orientation=horizontal]:overflow-x-auto data-[orientation=horizontal]:px-2 data-[orientation=vertical]:w-full data-[orientation=vertical]:flex-col data-[orientation=vertical]:overflow-y-auto data-[orientation=vertical]:py-1",
    },
  },
  defaultVariants: { variant: "default" },
});

const tabsTriggerVariants = cva("", {
  variants: {
    variant: {
      default:
        "inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-full border border-border px-2 py-2 font-body text-sm font-medium whitespace-nowrap text-muted-foreground transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-border/50 focus-visible:outline-1 focus-visible:outline-border disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-secondary data-[state=active]:text-primary data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      // One campaign in the rail. Selected takes --primary as a frame (a
      // background, which the global mouse-focus reset can't strip the way
      // it strips box-shadow rings), plus an edge marker beside the chip so
      // selection isn't carried by colour alone; keyboard focus is the shared
      // `.focus-ring`. No ring/translate utilities: Tailwind's @property
      // variables behind them don't register inside a shadow root.
      chip: "focus-ring relative size-7 shrink-0 cursor-pointer rounded-(--border-radius-inner) bg-background-card-surface p-0.5 transition-colors before:absolute before:rounded-full before:bg-primary before:opacity-0 before:content-[''] data-[orientation=horizontal]:before:inset-x-0 data-[orientation=horizontal]:before:-bottom-2 data-[orientation=horizontal]:before:mx-auto data-[orientation=horizontal]:before:h-1 data-[orientation=horizontal]:before:w-4 data-[orientation=vertical]:before:inset-y-0 data-[orientation=vertical]:before:-left-2 data-[orientation=vertical]:before:my-auto data-[orientation=vertical]:before:h-4 data-[orientation=vertical]:before:w-1 data-[state=active]:bg-primary data-[state=active]:before:opacity-100 data-[state=inactive]:hover:bg-border motion-reduce:transition-none",
    },
  },
  defaultVariants: { variant: "default" },
});

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col gap-2 font-body", className)}
      {...props}
    />
  );
}

function TabsList({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> &
  VariantProps<typeof tabsTriggerVariants>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
